import React from 'react';
import { Database, Plus, Link2, FileText, FileSpreadsheet, File, ExternalLink } from 'lucide-react';
import { PageShell, PageHeader, Badge } from '../components/PeecUI';
import { useI18n } from '../i18n/useI18n';

interface MaterialsPageProps {
  workspaceId: string;
}

const FILE_ICON: Record<string, React.ReactNode> = {
  PDF: <FileText size={15} className="text-rose-500" />,
  DOCX: <File size={15} className="text-sky-500" />,
  CSV: <FileSpreadsheet size={15} className="text-emerald-500" />,
  TXT: <FileText size={15} className="text-zinc-400" />,
};

const MATERIALS = [
  {
    id: 'm-1',
    name: '品牌事实手册 2026',
    type: 'PDF',
    source: 'Drive',
    updatedAt: '2026-05-20',
    size: '1.2 MB',
    status: 'available',
  },
  {
    id: 'm-2',
    name: '客户案例摘要集',
    type: 'DOCX',
    source: 'Notion',
    updatedAt: '2026-05-18',
    size: '340 KB',
    status: 'available',
  },
  {
    id: 'm-3',
    name: '已审核产品声明',
    type: 'CSV',
    source: 'Upload',
    updatedAt: '2026-05-12',
    size: '88 KB',
    status: 'available',
  },
  {
    id: 'm-4',
    name: '品牌调性指南',
    type: 'PDF',
    source: 'Drive',
    updatedAt: '2026-05-10',
    size: '2.5 MB',
    status: 'available',
  },
  {
    id: 'm-5',
    name: '目标市场受众画像',
    type: 'DOCX',
    source: 'Upload',
    updatedAt: '2026-05-08',
    size: '210 KB',
    status: 'available',
  },
  {
    id: 'm-6',
    name: '竞品对比研究报告',
    type: 'PDF',
    source: 'Drive',
    updatedAt: '2026-04-30',
    size: '3.1 MB',
    status: 'available',
  },
];

const SOURCE_BADGE: Record<string, 'slate' | 'blue' | 'green' | 'amber' | 'purple'> = {
  Drive: 'blue',
  Notion: 'purple',
  Upload: 'slate',
};

const MaterialsPage: React.FC<MaterialsPageProps> = ({ workspaceId: _workspaceId }) => {
  const { t } = useI18n();

  return (
    <PageShell>
      <PageHeader title={t('materials.title')} description={t('materials.description')} />

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            <Database size={16} className="text-zinc-500" aria-hidden="true" />
            {t('materials.library')}
            <span className="ml-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500">
              {MATERIALS.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400">
              <Link2 size={13} aria-hidden="true" />
              {t('materials.connectSource')}
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white outline-none transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400">
              <Plus size={13} aria-hidden="true" />
              {t('materials.addMaterial')}
            </button>
          </div>
        </div>

        {/* Header row */}
        <div className="grid grid-cols-12 items-center border-b border-zinc-100 bg-zinc-50 px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-zinc-400">
          <div className="col-span-5">名称</div>
          <div className="col-span-2">类型</div>
          <div className="col-span-2">来源</div>
          <div className="col-span-1">大小</div>
          <div className="col-span-1">更新时间</div>
          <div className="col-span-1 text-right">状态</div>
        </div>

        <div className="divide-y divide-zinc-100">
          {MATERIALS.map((item) => (
            <div key={item.id} className="grid grid-cols-12 items-center px-5 py-3 text-sm hover:bg-zinc-50 transition-colors">
              <div className="col-span-5 flex items-center gap-2.5 font-medium text-zinc-900">
                {FILE_ICON[item.type] ?? <File size={15} className="text-zinc-400" />}
                <span>{item.name}</span>
              </div>
              <div className="col-span-2">
                <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">{item.type}</span>
              </div>
              <div className="col-span-2">
                <Badge tone={SOURCE_BADGE[item.source] ?? 'slate'}>{item.source}</Badge>
              </div>
              <div className="col-span-1 text-xs text-zinc-500">{item.size}</div>
              <div className="col-span-1 text-xs text-zinc-400">{item.updatedAt}</div>
              <div className="col-span-1 flex justify-end items-center gap-2">
                <span className="text-xs text-emerald-600 font-medium">{t('materials.available')}</span>
                <button aria-label="打开文件" className="text-zinc-300 hover:text-zinc-600 transition-colors">
                  <ExternalLink size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
};

export default MaterialsPage;
